
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  useTheme,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimaryButton } from 'components/button/BglButtons';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { CustomerDataApis } from 'apis/customerDataAPIs';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

interface AddNewUserComponentProps {
  onBack?: () => void;
}

export const AddNewUserComponent = ({ onBack }: AddNewUserComponentProps) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [uuids, setUuids] = useState('');
  const [stagedUuids, setStagedUuids] = useState<string[]>([]);
  const [selectedMeterTypes, setSelectedMeterTypes] = useState<string>('');
  const [meterFuelTypes, setMeterFuelTypes] = useState<string[]>([]);
  const [loadingMeterTypes, setLoadingMeterTypes] = useState<boolean>(false);
  const [meterTypesError, setMeterTypesError] = useState<string>('');
  const [userMeterFuelResults, setUserMeterFuelResults] = useState<Array<{
    source_uuid: string;
    valid_meter_fuels: string[];
    status: string;
  }>>([]);
  const [loadingValidMeterFuels, setLoadingValidMeterFuels] = useState<boolean>(false);
  const [validMeterFuelsError, setValidMeterFuelsError] = useState<string>('');
  const [hasValidatedMeterFuels, setHasValidatedMeterFuels] = useState<boolean>(false);
  const [userMeterFuelSelections, setUserMeterFuelSelections] = useState<Record<string, string[]>>({});

  const handleBackToManageUsers = () => {
    if (onBack) {
      onBack();
    } else {
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR);
    }
  };

  const getEnvironmentConfig = (envId: string) => {
    return environments.find(env => env.id === envId);
  };

  const handleNext = () => {
    const envConfig = getEnvironmentConfig(selectedEnvironment);
    console.log('Selected Environment Config:', envConfig);
    console.log('Staged UUIDs:', stagedUuids);
    console.log('User Meter Fuel Selections:', userMeterFuelSelections);
    
    if (!envConfig) {
      console.error('Environment configuration not found');
      return;
    }

    const validUuids = stagedUuids.filter(uuid => {
      const selections = userMeterFuelSelections[uuid];
      if (Array.isArray(selections)) {
        return selections.length > 0;
      }
      return selections && String(selections).trim() !== '';
    });

    const validSelections: Record<string, string[]> = {};
    validUuids.forEach(uuid => {
      const selections = userMeterFuelSelections[uuid];
      validSelections[uuid] = Array.isArray(selections) ? selections : [selections];
    });

    const uniqueMeterFuelTypes = [...new Set(Object.values(validSelections).flat())];

    console.log('Valid UUIDs (with supported meter fuels):', validUuids);
    console.log('Valid Meter Fuel Selections:', validSelections);
    console.log('Unique Meter Fuel Types:', uniqueMeterFuelTypes);

    // Save user ingestion data to localStorage - only users with valid meter fuel selection
    const userIngestionData = {
      selectedEnvironment,
      stagedUuids: validUuids,
      userMeterFuelSelections: validSelections,
      selectedMeterTypes: uniqueMeterFuelTypes,
      environmentConfig: envConfig,
      timestamp: Date.now() 
    };
    
    try {
      localStorage.setItem('userIngestionData', JSON.stringify(userIngestionData));
      console.log('Saved user ingestion data to localStorage:', userIngestionData);
      
      // Navigate to update metadata page - API will be called there on page load
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.UPDATE_METADATA);
    } catch (error) {
      console.error('Failed to save data to localStorage:', error);
      alert('Failed to save data. Please try again.');
    }
  };

  const handleUuidInput = (value: string) => {
    setUuids(value);
  };

  const handleUuidKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      stageUuids();
    }
  };

  const stageUuids = () => {
    if (!uuids.trim()) return;
    
    const inputUuids = uuids
      .split(/[,\n]/)
      .map(uuid => uuid.trim())
      .filter(uuid => uuid);
    
    const uniqueInputUuids = [...new Set(inputUuids)];
    
    const newUuids = uniqueInputUuids.filter(uuid => !stagedUuids.includes(uuid));
    
    if (newUuids.length > 0) {
      setStagedUuids(prev => [...prev, ...newUuids]);
      setUuids('');
      // Reset validation when new UUIDs are added
      setHasValidatedMeterFuels(false);
      setUserMeterFuelResults([]);
      setUserMeterFuelSelections(prev => {
        const newSelections = { ...prev };
        newUuids.forEach(uuid => {
          if (!newSelections[uuid]) {
            newSelections[uuid] = [];
          }
        });
        return newSelections;
      });
    }
  };

  const removeStagedUuid = (uuidToRemove: string) => {
    setStagedUuids(prev => prev.filter(uuid => uuid !== uuidToRemove));
    // Reset validation when UUIDs change
    setHasValidatedMeterFuels(false);
    setUserMeterFuelResults([]);
    setSelectedMeterTypes('');
    setUserMeterFuelSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[uuidToRemove];
      return newSelections;
    });
  };

  const handleUserMeterFuelChange = (uuid: string, meterFuel: string) => {
    setUserMeterFuelSelections(prev => {
      const rawSelections = prev[uuid];
      const currentSelections = Array.isArray(rawSelections) 
        ? rawSelections 
        : (rawSelections ? [rawSelections] : []);
      
      const isSelected = currentSelections.includes(meterFuel);
      return {
        ...prev,
        [uuid]: isSelected 
          ? currentSelections.filter(f => f !== meterFuel) 
          : [...currentSelections, meterFuel] 
      };
    });
  };

  // Function to fetch meter fuel types from ingestion config
  const fetchMeterFuelTypes = async () => {
    if (!selectedPilotId) {
      console.warn('No pilot selected for fetching meter fuel types');
      return;
    }

    try {
      setLoadingMeterTypes(true);
      setMeterTypesError('');
      const response = await PilotApis.getIngestionConfigs(rc, selectedPilotId.toString(), {
        level: 'pilot' as string
      });
      
      if (response.success && response.data) {
        let supportedMeterTypes: string[] = [];
        let supportedFuels: string[] = [];
        
        const meterTypesConfig = response.data.find(config => 
          config.configKey === 'supported_meter_types'
        );
        console.log('Meter types config found:', meterTypesConfig);
        // Find allowed_measurement_type config
        const measurementTypesConfig = response.data.find(config => 
          config.configKey === 'allowed_measurement_type'
        );
        
        if (!meterTypesConfig) {
          setMeterTypesError('Meter types configuration not found. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        if (!measurementTypesConfig) {
          setMeterTypesError('Measurement types configuration not found. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        // Parse supported meter types from defaultValue (JSON string)
        if (meterTypesConfig?.defaultValue) {
          try {
            let rawValue = meterTypesConfig.defaultValue;
            
            // Handle "N/A" or invalid values
            if (rawValue === 'N/A' || rawValue === 'null' || rawValue === 'undefined') {
              setMeterTypesError('Meter types configuration contains invalid data. Please check pilot configuration <supported_meter_types>.');
              setMeterFuelTypes([]);
              return;
            }
            
            if (rawValue.includes('\\\\"')) {
              rawValue = rawValue.replace(/\\\\"/g, '\\"');
              console.log('After handling double-escaped quotes:', rawValue);
            } else if (rawValue.includes('\\"')) {
              rawValue = rawValue.replace(/\\"/g, '"');
              console.log('After handling single-escaped quotes:', rawValue);
            }
            
            const parsedMeterTypes = JSON.parse(rawValue);
            console.log('Parsed meter types:', parsedMeterTypes);
            
            if (Array.isArray(parsedMeterTypes)) {
              supportedMeterTypes = parsedMeterTypes;
            } else {
              setMeterTypesError('Meter types configuration is not in expected format. Please check pilot configuration.');
              setMeterFuelTypes([]);
              return;
            }
          } catch (parseError) {
            console.error('Failed to parse supported_meter_types:', parseError);
            const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown parsing error';
            setMeterTypesError(`Failed to parse meter types configuration: ${errorMessage}. Please check pilot configuration.`);
            setMeterFuelTypes([]);
            return;
          }
        } else {
          setMeterTypesError('Meter types configuration is empty. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        if (measurementTypesConfig?.defaultValue) {
          const measurementTypes = measurementTypesConfig.defaultValue.includes(',') 
            ? measurementTypesConfig.defaultValue.split(',').map((type: string) => type.trim())
            : [measurementTypesConfig.defaultValue];
          supportedFuels = measurementTypes;
        } else {
          setMeterTypesError('Measurement types configuration is empty. Please check pilot configuration <allowed_measurement_type>');
          setMeterFuelTypes([]);
          return;
        }
        const combinations: string[] = [];
        supportedMeterTypes.forEach(meterType => {
          supportedFuels.forEach(fuel => {
            let fuelUpper = fuel.toUpperCase().trim();
            if (fuelUpper === 'ELECTRICITY') {
              fuelUpper = 'ELECTRIC';
            }
            combinations.push(`${meterType}-${fuelUpper}`);
          });
        });
        
        setMeterFuelTypes(combinations);
      } else {
        console.error('Failed to fetch ingestion configs:', response.error);
        setMeterTypesError('Failed to fetch pilot configuration. Please try again.');
        setMeterFuelTypes([]);
      }
    } catch (error) {
      console.error('Error fetching meter fuel types:', error);
      setMeterTypesError('Error fetching meter types configuration. Please try again.');
      setMeterFuelTypes([]);
    } finally {
      setLoadingMeterTypes(false);
    }
  };

  // Function to fetch valid meter fuels from API
  const fetchValidMeterFuels = async () => {
    // First stage any pending UUIDs
    let uuidsToValidate = [...stagedUuids];
    
    if (uuids.trim()) {
      const newUuids = uuids
        .split(/[,\n]/)
        .map(uuid => uuid.trim())
        .filter(uuid => uuid && !stagedUuids.includes(uuid));
      
      if (newUuids.length > 0) {
        uuidsToValidate = [...stagedUuids, ...newUuids];
        setStagedUuids(uuidsToValidate);
        setUuids('');
      }
    }
    
    // Call API with all UUIDs
    if (uuidsToValidate.length > 0) {
      await callValidMeterFuelApi(uuidsToValidate);
    }
  };

  const callValidMeterFuelApi = async (uuidsToValidate: string[]) => {
    if (!selectedPilotId || uuidsToValidate.length === 0 || !selectedEnvironment) {
      setUserMeterFuelResults([]);
      setHasValidatedMeterFuels(false);
      return;
    }

    try {
      setLoadingValidMeterFuels(true);
      setValidMeterFuelsError('');
      setHasValidatedMeterFuels(false);
      setUserMeterFuelSelections({});
      
      const response = await CustomerDataApis.fetchValidMeterFuel(rc, {
        destination_pilot_id: selectedEnvironment,
        source_uuids: uuidsToValidate
      });

      if (response.success && response.data) {
        // Store the full results for per-UUID display
        if (response.data.results && Array.isArray(response.data.results)) {
          const results = response.data.results.map((result: any) => ({
            source_uuid: result.source_uuid,
            valid_meter_fuels: result.valid_meter_fuels || [],
            status: result.status
          }));
          setUserMeterFuelResults(results);
          
          // Auto-select if user has only one supported fuel option
          const autoSelections: Record<string, string[]> = {};
          results.forEach((result: any) => {
            const supportedFuels = result.valid_meter_fuels.filter((fuel: string) =>
              meterFuelTypes.some(configFuel => 
                configFuel.toUpperCase() === fuel.toUpperCase()
              )
            );
            if (supportedFuels.length === 1) {
              autoSelections[result.source_uuid] = [supportedFuels[0]];
            }
          });
          setUserMeterFuelSelections(autoSelections);
        }
        
        setHasValidatedMeterFuels(true);
      } else {
        console.error('Failed to fetch valid meter fuels:', response.error);
        setValidMeterFuelsError('Failed to fetch valid meter fuels for the provided UUIDs.');
        setUserMeterFuelResults([]);
        setHasValidatedMeterFuels(false);
      }
    } catch (error) {
      console.error('Error fetching valid meter fuels:', error);
      setValidMeterFuelsError('Error fetching valid meter fuels. Please try again.');
      setUserMeterFuelResults([]);
      setHasValidatedMeterFuels(false);
    } finally {
      setLoadingValidMeterFuels(false);
    }
  };

  // Check if all users with supported fuels have made a selection
  const allUsersHaveSelection = React.useMemo(() => {
    if (!hasValidatedMeterFuels || userMeterFuelResults.length === 0) {
      return false;
    }
    
    return userMeterFuelResults.every(result => {
      const supportedFuels = result.valid_meter_fuels.filter(fuel =>
        meterFuelTypes.some(configFuel => 
          configFuel.toUpperCase() === fuel.toUpperCase()
        )
      );
      if (supportedFuels.length === 0) return true;
      const selections = userMeterFuelSelections[result.source_uuid];
      if (Array.isArray(selections)) {
        return selections.length > 0;
      }
      return selections && String(selections).trim() !== '';
    });
  }, [hasValidatedMeterFuels, userMeterFuelResults, meterFuelTypes, userMeterFuelSelections]);

  const hasAnySupportedFuels = React.useMemo(() => {
    if (!hasValidatedMeterFuels || userMeterFuelResults.length === 0) {
      return false;
    }
    
    return userMeterFuelResults.some(result => 
      result.valid_meter_fuels.some(fuel =>
        meterFuelTypes.some(configFuel => 
          configFuel.toUpperCase() === fuel.toUpperCase()
        )
      )
    );
  }, [hasValidatedMeterFuels, userMeterFuelResults, meterFuelTypes]);

  // Check if at least one user has made a meter fuel selection
  const atLeastOneUserHasSelection = React.useMemo(() => {
    return Object.values(userMeterFuelSelections).some(fuels => {
      if (Array.isArray(fuels)) {
        return fuels.length > 0;
      }
      return fuels && String(fuels).trim() !== '';
    });
  }, [userMeterFuelSelections]);

  // Check if the arrow button should be enabled
  const canValidateMeterFuels = selectedEnvironment && (stagedUuids.length > 0 || uuids.trim().length > 0);

  useEffect(() => {
    if (selectedPilotId) {
      fetchMeterFuelTypes();
    }
  }, [selectedPilotId]);

  // Environment configurations
  const environments = [
    { 
      id: 'Nonprod', 
      name: 'Nonprod',
      baseUrl: 'https://nonprodqaapi-external.bidgely.com',
      username: 'irfan@bidgely.com',
      password: 'bZgBogil'
    },
    { 
      id: 'NA', 
      name: 'NA',
      baseUrl: 'https://naapi.bidgely.com',
      username: 'navneetnipu@bidgely.com',
      password: 'vesVZiTj'
    },
    { 
      id: 'NA2', 
      name: 'NA2',
      baseUrl: 'https://naapi2-external.bidgely.com',
      username: 'irfan@bidgely.com',
      password: '7dKLYivX'
    },
    { 
      id: 'EU', 
      name: 'EU',
      baseUrl: 'https://euapi.bidgely.com',
      username: 'irfan@bidgely.com',
      password: 'YS6PyhLi'
    },
    { 
      id: 'CA', 
      name: 'CA',
      baseUrl: 'https://caapi.bidgely.com',
      username: 'irfan@bidgely.com',
      password: '438qHzru'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 4,
        textAlign: 'left'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
          <ManageAccountsOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
          Ingest Production User
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToManageUsers}
          variant="outlined"
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          Back to Manage Users
        </Button>
      </Box>
      <Box sx={{ 
          mt: 2,
          mb: 2,
          p: 2,
          bgcolor: theme.palette.info.light + '20',
          textAlign: 'left'
        }}>
          <Typography variant="body2" sx={{ 
            color: theme.palette.info.dark,
            fontWeight: 500,
            lineHeight: 1.5
          }}>
            This tool ingests the customer data in standard Bidgely format. Custom transformers are not supported. Hence, this tool should be used for new pilots before custom transformers are implemented.
          </Typography>
        </Box>
      <Box sx={{ 
           mt: 2,
           mb: 2,
           p: 2,
           bgcolor: theme.palette.info.light + '20',
           textAlign: 'left'
        }}>
          <Typography variant="body2" sx={{ 
            color: theme.palette.info.dark,
            fontWeight: 500,
            lineHeight: 1.5
          }}>
            This tool is not designed to support bulk ingestion or large-scale user creation. Kindly restrict the number of users to 2.
          </Typography>
        </Box>

      {/* Choose Source Environment Section */}
      <Card sx={{ 
        mb: 4,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
              <ViewStreamOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
              Choose Source Environment
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
              Please select an environment to continue with user ingestion.
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
              Environment
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
                displayEmpty
                sx={{
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select an environment
                </MenuItem>
                {environments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Choose Users Section */}
      <Card sx={{ 
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
              {/* <PeopleAltOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} /> */}
              Choose Users From The Source Project
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
              Provide user identifiers. Data will be ingested for the last 2 years.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
              Enter UUID
            </Typography>
            <TextField
              fullWidth
              rows={2}
              placeholder="Type a UUID and Press Enter to add ..."
              value={uuids}
              onChange={(e) => handleUuidInput(e.target.value)}
              onKeyPress={handleUuidKeyPress}
              onBlur={stageUuids}
              variant="outlined"
              disabled={loadingValidMeterFuels}
            />
          </Box>

          {stagedUuids.length > 0 && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
                Staged UUIDs ({stagedUuids.length})
              </Typography>
              <Box sx={{ 
                border: `1px solid ${theme.palette.primary.main}40`,
                borderRadius: 2,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : theme.palette.grey[50],
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5
              }}>
                {stagedUuids.map((uuid, index) => (
                  <Chip
                    key={index}
                    label={uuid}
                    onDelete={() => removeStagedUuid(uuid)}
                    size="medium"
                    sx={{
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.primary.main + '60',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(51, 65, 85, 0.8)' : theme.palette.grey[100],
                      borderRadius: '50px',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      height: '36px',
                      px: 1,
                      border: `1px solid ${theme.palette.primary.main}40`,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.text.secondary,
                        fontSize: '18px',
                        '&:hover': {
                          color: theme.palette.error.main,
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Per-UUID Meter Fuel Selection Table - shown only after validation */}
          {hasValidatedMeterFuels && userMeterFuelResults.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                {/* Table Header */}
                <Box sx={{ 
                  display: 'flex', 
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : theme.palette.grey[100],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  py: 1,
                  px: 2
                }}>
                  <Typography variant="subtitle2" sx={{ width: '310px', fontWeight: 600, borderRight: `1px solid ${theme.palette.divider}`, pr: 2, mr: 2 }}>
                    UUID
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Meter Fuel
                  </Typography>
                </Box>
                
                {/* Table Rows */}
                {userMeterFuelResults.map((userResult, index) => {
                  const supportedFuels = userResult.valid_meter_fuels.filter(fuel =>
                    meterFuelTypes.some(configFuel => 
                      configFuel.toUpperCase() === fuel.toUpperCase()
                    )
                  );
                  const unsupportedFuels = userResult.valid_meter_fuels.filter(fuel =>
                    !meterFuelTypes.some(configFuel => 
                      configFuel.toUpperCase() === fuel.toUpperCase()
                    )
                  );
                  const rawFuels = userMeterFuelSelections[userResult.source_uuid];
                  const selectedFuels = Array.isArray(rawFuels) ? rawFuels : (rawFuels ? [rawFuels] : []);

                  return (
                    <Box 
                      key={userResult.source_uuid}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        py: 1.5,
                        px: 2,
                        borderBottom: index < userMeterFuelResults.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      {/* UUID Column */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          width: '310px',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          borderRight: `1px solid ${theme.palette.divider}`,
                          pr: 2,
                          mr: 2,
                        }}
                      >
                        {userResult.source_uuid}
                      </Typography>
                      
                      {/* Meter Fuel Column */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {supportedFuels.map((fuel) => (
                          <FormControlLabel
                            key={fuel}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedFuels.includes(fuel)}
                                onChange={() => handleUserMeterFuelChange(userResult.source_uuid, fuel)}
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={fuel}
                            sx={{
                              m: 0,
                              mr: 1,
                              '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                            }}
                          />
                        ))}
                        {unsupportedFuels.map((fuel) => (
                          <FormControlLabel
                            key={fuel}
                            control={
                              <Checkbox
                                size="small"
                                disabled
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={fuel}
                            sx={{
                              m: 0,
                              mr: 1,
                              '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Warning when no users have supported meter fuels */}
          {hasValidatedMeterFuels && !hasAnySupportedFuels && (
            <Box sx={{ 
              mb: 3,
              p: 2, 
              bgcolor: theme.palette.warning.light + '20',
              border: `1px solid ${theme.palette.warning.main}`,
              borderRadius: 1,
              textAlign: 'left'
            }}>
              <Typography variant="body2" sx={{ 
                color: theme.palette.warning.main,
                fontWeight: 500,
                lineHeight: 1.5
              }}>
                No matching meter types found. None of the selected UUIDs have meter fuels that match the pilot configuration ({meterFuelTypes.join(', ')}).
              </Typography>
            </Box>
          )}

          {/* Check User Details Button - shown before validation */}
          {!hasValidatedMeterFuels && (
            <Box sx={{ display: 'flex', width: '100%' }}>
              <BglPrimaryButton
                onClick={fetchValidMeterFuels}
                disabled={!canValidateMeterFuels || loadingValidMeterFuels}
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    color: 'white'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    opacity: 0.5,
                    color: 'white'
                  }
                }}
              >
                {loadingValidMeterFuels ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    Checking...
                  </Box>
                ) : (
                  'Check User Details'
                )}
              </BglPrimaryButton>
            </Box>
          )}

          {/* Next: Edit Button - shown when at least one user has meter fuel selected */}
          {hasValidatedMeterFuels && atLeastOneUserHasSelection && hasAnySupportedFuels && (
            <Box sx={{ display: 'flex', width: '100%' }}>
              <BglPrimaryButton
                onClick={handleNext}
                disabled={
                  !selectedEnvironment || 
                  stagedUuids.length === 0 || 
                  !atLeastOneUserHasSelection
                }
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    color: 'white'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    opacity: 0.5,
                    color: 'white'
                  }
                }}
              >
                Next: Edit Info & Set Billing
              </BglPrimaryButton>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

